import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { FadeIn, FadeInLeft, FadeInRight, StaggerContainer, StaggerItem } from '../components/motion/FadeIn';
import { SectionLabel } from '../components/shared/SectionLabel';
import { CTASection } from '../components/shared/CTASection';
import { IMAGES } from '../lib/images';
import {
  ArrowRight, CheckCircle2, Send, UserPlus, Target, CreditCard,
  Bell, Heart, Smartphone, Eye, BarChart3, Mail, Zap
} from 'lucide-react';



const HeroSection = () => (
  <section className="relative overflow-hidden pt-20 sm:pt-24 pb-14 sm:pb-16">
    <div className="absolute inset-0 -z-10" style={{
      background: 'radial-gradient(1200px circle at 20% 10%, rgba(42,140,134,0.12), transparent 55%)'
    }} />
    <div className="mx-auto max-w-[var(--grid-max)] px-4 sm:px-6 lg:px-8">
      <div className="grid gap-10 lg:grid-cols-2 items-center">
        <FadeInLeft>
          <SectionLabel>How It Works</SectionLabel>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-[hsl(var(--sss-navy))]">
            Here's Exactly How Smile Score Solutions Works Inside Your Practice
          </h1>
          <p className="mt-5 text-base md:text-lg text-[hsl(var(--sss-ink))]/70">
            From the moment a patient is declined to the moment they return approved — this is the system working for you.
          </p>
        </FadeInLeft>
        <FadeInRight>
          <div className="rounded-2xl overflow-hidden shadow-[var(--shadow-lg)] h-64 sm:h-80">
            <img src={IMAGES.dentistConsulting} alt="Dental consultation" className="w-full h-full object-cover" loading="lazy" />
          </div>
        </FadeInRight>
      </div>
    </div>
  </section>
);

const WorkflowTimeline = () => {
  const steps = [
    { num: 1, icon: <CreditCard className="h-5 w-5" />, title: 'Patient Is Declined for Financing', desc: 'A patient sits in your chair. They want treatment. They apply for CareCredit, Proceed, LendingClub, or another financing option — and they\'re declined. In most practices, this is where the relationship ends. With Smile Score Solutions, it\'s where recovery begins.' },
    { num: 2, icon: <Send className="h-5 w-5" />, title: 'Your Team Sends the Enrollment Link', desc: 'Your treatment coordinator or front desk sends the patient a link via text or email. The link takes them to your practice-branded Smile Score Solutions portal. This takes less than 30 seconds.' },
    { num: 3, icon: <UserPlus className="h-5 w-5" />, title: 'Patient Creates Their Account', desc: 'The patient signs up on your branded portal. They complete a quick onboarding that assesses their current financial situation and sets a goal aligned with common financing approval thresholds.' },
    { num: 4, icon: <Target className="h-5 w-5" />, title: 'Guided Improvement Begins', desc: 'The patient receives a personalized action plan with specific tasks, timelines, and projected score impact. This includes steps around disputing inaccuracies, managing debt, and building credit.' },
    { num: 5, icon: <BarChart3 className="h-5 w-5" />, title: 'Credit Monitoring Tracks Progress', desc: 'The patient receives ongoing credit monitoring powered by data from top credit bureaus. They can see their score change over time and stay motivated to continue.' },
    { num: 6, icon: <Bell className="h-5 w-5" />, title: 'Your Practice Gets Alerts', desc: 'When a patient\'s score improves significantly or crosses a key threshold, your practice receives an alert. This means your team knows exactly when to follow up.' },
    { num: 7, icon: <Heart className="h-5 w-5" />, title: 'Patient Returns for Treatment', desc: 'Your team reaches out. The patient is ready. They apply for financing again — this time with a stronger score. They get approved. Treatment is scheduled. Revenue is recovered.' },
  ];

  return (
    <section className="py-16 sm:py-20">
      <div className="mx-auto max-w-[var(--grid-max)] px-4 sm:px-6 lg:px-8">
        <FadeIn className="text-center max-w-3xl mx-auto mb-12">
          <SectionLabel>The Full Workflow</SectionLabel>
          <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight text-[hsl(var(--sss-navy))]">From Decline to Recovery in 7 Steps</h2>
        </FadeIn>
        <div className="relative max-w-3xl mx-auto">
          <div className="absolute left-4 sm:left-6 top-0 bottom-0 w-px bg-[hsl(var(--border))]" />
          <div className="space-y-6">
            {steps.map((step, i) => (
              <FadeIn key={i} delay={i * 0.05}>
                <div data-testid="workflow-timeline-step" className="relative flex gap-4 sm:gap-6">
                  <div className="relative z-10 flex-shrink-0">
                    <div className="h-8 w-8 sm:h-12 sm:w-12 rounded-full bg-[hsl(var(--sss-navy))] text-white flex items-center justify-center ring-4 ring-white text-sm sm:text-base font-bold">
                      {step.num}
                    </div>
                  </div>
                  <Card className="flex-1 rounded-2xl border border-[hsl(var(--border))] bg-white shadow-[var(--shadow-sm)] hover:shadow-[var(--shadow-md)] transition-shadow">
                    <CardContent className="p-5">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-[hsl(var(--sss-teal))]">{step.icon}</span>
                        <h3 className="text-base sm:text-lg font-semibold text-[hsl(var(--sss-navy))]">{step.title}</h3>
                      </div>
                      <p className="text-sm text-muted-foreground leading-relaxed">{step.desc}</p>
                    </CardContent>
                  </Card>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const IntegrationSection = () => (
  <section className="py-16 sm:py-20 bg-[hsl(var(--secondary))]">
    <div className="mx-auto max-w-[var(--grid-max)] px-4 sm:px-6 lg:px-8">
      <div className="grid gap-10 lg:grid-cols-2 items-start">
        <FadeInLeft>
          <SectionLabel>Integration & Setup</SectionLabel>
          <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight text-[hsl(var(--sss-navy))]">Works With Your Existing Workflow</h2>
          <p className="mt-4 text-base text-[hsl(var(--sss-ink))]/70 leading-relaxed">Smile Score Solutions integrates with your existing systems and workflows. We offer API access and automation support so the platform can work with your current marketing tools, CRM, or patient management system.</p>
          <div className="mt-6 rounded-2xl overflow-hidden shadow-[var(--shadow-md)] h-48">
            <img src={IMAGES.dentalChair} alt="Modern dental clinic" className="w-full h-full object-cover" loading="lazy" />
          </div>
        </FadeInLeft>
        <FadeInRight>
          <div className="space-y-6">
            <Card className="rounded-2xl border border-[hsl(var(--border))] bg-white shadow-[var(--shadow-sm)]">
              <CardContent className="p-6">
                <h3 className="text-base font-semibold text-[hsl(var(--sss-navy))] mb-4">Setup Details</h3>
                <div className="space-y-3">
                  {['Onboarding takes less than one day','Your branded portal is configured for you','Your team receives training materials and scripts','Ongoing support is included'].map((item, i) => (
                    <div key={i} className="flex gap-3 text-sm text-[hsl(var(--sss-ink))]/80"><CheckCircle2 className="h-4 w-4 text-[hsl(var(--sss-teal))] shrink-0 mt-0.5" />{item}</div>
                  ))}
                </div>
              </CardContent>
            </Card>
            <Card className="rounded-2xl border border-[hsl(var(--border))] bg-white shadow-[var(--shadow-sm)]">
              <CardContent className="p-6">
                <h3 className="text-base font-semibold text-[hsl(var(--sss-navy))] mb-4">Three Ways to Enroll Patients</h3>
                <div className="space-y-3">
                  {[{icon:<Smartphone className="h-4 w-4"/>,text:'Send invites directly from inside the dashboard'},{icon:<Mail className="h-4 w-4"/>,text:'Use your unique practice link via text or email'},{icon:<Zap className="h-4 w-4"/>,text:'Integrate with your CRM for hands-free enrollment'}].map((item, i) => (
                    <div key={i} className="flex gap-3 text-sm text-[hsl(var(--sss-ink))]/80"><span className="text-[hsl(var(--sss-teal))] shrink-0 mt-0.5">{item.icon}</span>{item.text}</div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </FadeInRight>
      </div>
    </div>
  </section>
);

const PatientExperience = () => (
  <section className="py-16 sm:py-20">
    <div className="mx-auto max-w-[var(--grid-max)] px-4 sm:px-6 lg:px-8">
      <FadeIn className="text-center max-w-3xl mx-auto mb-12">
        <SectionLabel>Patient Experience</SectionLabel>
        <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight text-[hsl(var(--sss-navy))]">What Your Patients See</h2>
        <p className="mt-4 text-base text-[hsl(var(--sss-ink))]/70 leading-relaxed">
          Patients never see Smile Score Solutions branding — they see yours. The portal is fully practice-branded, so every interaction reinforces your practice.
        </p>
      </FadeIn>

      <div className="grid gap-10 lg:grid-cols-2 items-start">
        <FadeInLeft>
          <div className="space-y-6">
            {/* Track */}
            <Card className="rounded-2xl border border-[hsl(var(--border))] bg-white shadow-[var(--shadow-sm)]">
              <CardContent className="p-6">
                <span className="text-[10px] font-semibold uppercase tracking-widest text-[hsl(var(--sss-teal))]">Credit Monitoring</span>
                <h3 className="text-lg font-bold text-[hsl(var(--sss-navy))] mt-1 mb-3">Track</h3>
                <div className="space-y-2">
                  {['Weekly Equifax Credit Report', 'Daily Credit Monitoring', 'Score Tracking & Trends'].map((item, i) => (
                    <div key={i} className="flex gap-3 text-sm text-[hsl(var(--sss-ink))]/80">
                      <CheckCircle2 className="h-4 w-4 text-[hsl(var(--sss-teal))] shrink-0 mt-0.5" />{item}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
            {/* Grow */}
            <Card className="rounded-2xl border-2 border-[hsl(var(--sss-teal))]/30 bg-white shadow-[var(--shadow-md)] relative">
              <div className="absolute -top-3 right-6">
                <Badge className="bg-[hsl(var(--sss-teal))] text-white text-xs px-3 py-1">Most Popular</Badge>
              </div>
              <CardContent className="p-6">
                <span className="text-[10px] font-semibold uppercase tracking-widest text-[hsl(var(--sss-teal))]">Credit Monitoring + AI</span>
                <h3 className="text-lg font-bold text-[hsl(var(--sss-navy))] mt-1 mb-3">Grow</h3>
                <p className="text-xs text-muted-foreground mb-3 italic">Everything in Track, plus:</p>
                <div className="space-y-2">
                  {['AI Credit Agent personalized to their profile', 'Step-by-step guidance on how to manage their credit health', 'Actionable insights updated as their credit changes'].map((item, i) => (
                    <div key={i} className="flex gap-3 text-sm text-[hsl(var(--sss-ink))]/80">
                      <Zap className="h-4 w-4 text-[hsl(var(--sss-coral))] shrink-0 mt-0.5" />{item}
                    </div>
                  ))}
                </div>
                <div className="mt-4 rounded-xl bg-[hsl(var(--accent))]/50 p-3">
                  <p className="text-xs text-[hsl(var(--sss-ink))]/60 italic">Patients with a personalized action plan improve their score up to 2x faster.</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </FadeInLeft>
        <FadeInRight>
          <div className="space-y-4">
            <div className="rounded-2xl overflow-hidden shadow-[var(--shadow-lg)] h-72">
              <img src={IMAGES.blondeSmiling} alt="Happy patient smiling" className="w-full h-full object-cover" loading="lazy" />
            </div>
            <Card className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--accent))] shadow-[var(--shadow-sm)]">
              <CardContent className="p-6">
                <div className="flex items-center gap-2 mb-3"><Eye className="h-5 w-5 text-[hsl(var(--sss-teal))]" /><span className="text-sm font-semibold text-[hsl(var(--sss-navy))]">Key Point</span></div>
                <p className="text-sm text-[hsl(var(--sss-ink))]/80 leading-relaxed">Patients receive this platform at no cost to them. You provide it as a value-added service. Practices on the <span className="font-semibold">Premium plan</span> can give their hottest leads full Grow access with AI-powered coaching.</p>
              </CardContent>
            </Card>
          </div>
        </FadeInRight>
      </div>
    </div>
  </section>
);

export default function HowItWorksPage() {
  return (
    <div>
      <HeroSection />
      <WorkflowTimeline />
      <IntegrationSection />
      <PatientExperience />
      <CTASection headline="Ready to See It in Action?" subheadline="Book a personalized demo to see how Smile Score Solutions works inside your practice." />
    </div>
  );
}
